import Draw from './Draw';
import Loader from './loader/Loader';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ENGINE_API_URL, HOSTS } from '../constants/platform';
import { getUser } from '../sources/user';
import type { UserResponseAPI } from '../types/user';
import { isNullOrUndefined } from '../utils/guard';
import { checkAndRestoreSession, initializeAuthlib } from '../utils/auth';
import { useNotificationDispatch } from './notifications/NotificationProvider';
import GovernmentPolicy from './authentication/GovernmentPolicy';

const redirectUserToSsoLogin = () => {
    const tenantId = window.location.pathname.split('/')[1];
    if (!isNullOrUndefined(tenantId)) {
        window.location.replace(
            `${
                ENGINE_API_URL || window.location.origin
            }/api/draw/1/authentication/saml/${tenantId}`,
        );
        return true;
    }

    return false;
};

initializeAuthlib();

const initializeUser = async (
    isGovernmentUrl: boolean,
    hasAcceptedGovermentPolicy: boolean,
): Promise<UserResponseAPI | null> => {
    // Clean up legacy 'flowUser' removed in FLOW-3303
    window.sessionStorage.removeItem('flowUser');

    let user: UserResponseAPI | null = null;
    const isSsoBookmark = new URLSearchParams(window.location.search).get('sso');
    try {
        // Attempt to get the currently logged in user. If this fails then the cookie they have is no good
        // or they don't have a cookie
        user = await getUser();

        return user;
    } catch (_ex) {
        // If an sso user bookmarks flow after logging in, we don't want them to hit the main login screen.
        if (
            isSsoBookmark &&
            window.location.pathname !== '/logged-out' &&
            redirectUserToSsoLogin()
        ) {
            return user;
        }

        // If we're on a government URL and haven't accepted the government policy
        // we don't want them to hit the main login screen.
        // We need the user to be able to read the government policy.
        if (isGovernmentUrl && !hasAcceptedGovermentPolicy) {
            return user;
        }

        // Stops the page continually refreshing if no flow user is found with the Okta details provided.
        if (
            window.location.pathname !== '/user-not-found' &&
            window.location.pathname !== '/logged-out'
        ) {
            const isAuthenticated = await checkAndRestoreSession();
            if (isAuthenticated) {
                return getUser();
            }
        }

        return null;
    }
};

const Root = () => {
    const navigate = useNavigate();
    const dispatchNotification = useNotificationDispatch();
    const [userIsInitialized, setUserIsInitialized] = useState(false);
    const [hasAcceptedGovernmentPolicy, setHasAcceptedGovermentPolicy] = useState(false);
    const isGovernmentUrl = window.location.hostname === HOSTS.federal;

    const acceptGovernmentPolicy = () => {
        setHasAcceptedGovermentPolicy(true);
    };

    const [user, setUser] = useState<UserResponseAPI | null>(null);

    const showGovernmentPolicy = userIsInitialized && user === null && isGovernmentUrl;

    useEffect(() => {
        initializeUser(isGovernmentUrl, hasAcceptedGovernmentPolicy)
            .then((user) => {
                setUser(user);
            })
            .catch((error) => {
                console.error('Error initializing user:', error);
                dispatchNotification({
                    type: 'notifyError',
                    payload: { error: 'Failed to initialize user authentication.' },
                });
                navigate('/logged-out');
            })
            .finally(() => setUserIsInitialized(true));
    }, [navigate, dispatchNotification, isGovernmentUrl, hasAcceptedGovernmentPolicy]);

    if (showGovernmentPolicy) {
        return <GovernmentPolicy acceptGovernmentPolicy={acceptGovernmentPolicy} />;
    }

    if (!userIsInitialized) {
        return <Loader />;
    }

    return <Draw user={user} />;
};
export default Root;
